import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';

export default function VehicleExpiryWarningModal({ show, expiringDocuments, onClose, onUpdate }) {
  const [updateMode, setUpdateMode] = useState(false);
  const [formData, setFormData] = useState({
    roadTaxExpiry: '',
    motExpiry: '',
    insuranceExpiry: '',
    vehicleInspectionExpiry: ''
  });
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      setUpdateError('');
      
      const token = localStorage.getItem('token');
      const updateData = {};
      
      // Only include fields that have been updated
      if (formData.roadTaxExpiry) updateData.roadTaxExpiry = formData.roadTaxExpiry;
      if (formData.motExpiry) updateData.motExpiry = formData.motExpiry;
      if (formData.insuranceExpiry) updateData.insuranceExpiry = formData.insuranceExpiry;
      if (formData.vehicleInspectionExpiry) updateData.vehicleInspectionExpiry = formData.vehicleInspectionExpiry;

      if (Object.keys(updateData).length === 0) {
        setUpdateError('Please update at least one expiry date');
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        '/auth/update-vehicle-expiry',
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        // Call the onUpdate callback to refresh data
        if (onUpdate) {
          onUpdate();
        }
        
        // Reset form and close update mode
        setFormData({
          roadTaxExpiry: '',
          motExpiry: '',
          insuranceExpiry: '',
          vehicleInspectionExpiry: ''
        });
        setUpdateMode(false);
        
        // Close the modal after successful update
        onClose();
      }
    } catch (error) {
      console.error('Error updating vehicle expiry dates:', error);
      setUpdateError('Failed to update expiry dates. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const getDaysUntilExpiryColor = (days) => {
    if (days < 0) return 'danger'; // Expired
    if (days <= 7) return 'danger'; // 1 week or less
    if (days <= 14) return 'warning'; // 2 weeks or less
    return 'info'; // 3 weeks or less
  };

  const getDaysUntilExpiryText = (days) => {
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`;
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `Expires in ${days} day${days !== 1 ? 's' : ''}`;
  };

  const getFieldNameForType = (type) => {
    const mapping = {
      roadTax: 'roadTaxExpiry',
      mot: 'motExpiry',
      insurance: 'insuranceExpiry',
      vehicleInspection: 'vehicleInspectionExpiry'
    };
    return mapping[type] || type;
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      backdrop="static" 
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header style={{ backgroundColor: '#dc3545', color: '#fff' }}>
        <Modal.Title className="w-100 text-center">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Vehicle Document Expiry Warning
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {!updateMode ? (
          <>
            <Alert variant="danger" className="mb-3">
              <strong>⚠️ Important Notice:</strong> The following vehicle documents are expiring soon or have already expired. 
              Please update them immediately to continue operating legally.
            </Alert>

            <div className="mb-3">
              <h6 className="text-muted mb-3">Expiring Documents:</h6>
              
              {expiringDocuments && expiringDocuments.map((doc, index) => (
                <div 
                  key={index} 
                  className="p-3 mb-2 border rounded"
                  style={{ 
                    backgroundColor: doc.daysUntilExpiry < 0 ? '#ffe6e6' : '#fff3cd' 
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        <i className="bi bi-file-earmark-text me-2"></i>
                        {doc.label}
                      </h6>
                      <p className="mb-1 small">
                        <strong>Expiry Date:</strong> {new Date(doc.expiryDate).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <Badge bg={getDaysUntilExpiryColor(doc.daysUntilExpiry)}>
                      {getDaysUntilExpiryText(doc.daysUntilExpiry)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            <Alert variant="info" className="mb-0">
              <small>
                <i className="bi bi-info-circle me-1"></i>
                You can acknowledge this warning and update the documents later from your Driver Profile, 
                or update them now using the button below.
              </small>
            </Alert>
          </>
        ) : (
          <>
            <Alert variant="warning" className="mb-3">
              <strong>Update Document Expiry Dates</strong>
              <p className="mb-0 small mt-2">
                Update the new expiry dates for your vehicle documents. Only fill in the fields you want to update.
              </p>
            </Alert>

            {updateError && (
              <Alert variant="danger" className="mb-3">
                {updateError}
              </Alert>
            )}

            <Form>
              {expiringDocuments && expiringDocuments.map((doc, index) => (
                <Form.Group key={index} className="mb-3">
                  <Form.Label>
                    <strong>{doc.label} Expiry Date</strong>
                    <Badge bg={getDaysUntilExpiryColor(doc.daysUntilExpiry)} className="ms-2">
                      Current: {new Date(doc.expiryDate).toLocaleDateString('en-GB')}
                    </Badge>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name={getFieldNameForType(doc.type)}
                    value={formData[getFieldNameForType(doc.type)]}
                    onChange={handleChange}
                  />
                </Form.Group>
              ))}
            </Form>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        {!updateMode ? (
          <>
            <Button 
              variant="secondary" 
              onClick={onClose}
            >
              Acknowledge & Close
            </Button>
            <Button 
              variant="primary" 
              onClick={() => setUpdateMode(true)}
            >
              <i className="bi bi-pencil-square me-1"></i>
              Update Documents Now
            </Button>
          </>
        ) : (
          <>
            <Button 
              variant="secondary" 
              onClick={() => {
                setUpdateMode(false);
                setUpdateError('');
                setFormData({
                  roadTaxExpiry: '',
                  motExpiry: '',
                  insuranceExpiry: '',
                  vehicleInspectionExpiry: ''
                });
              }}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={handleUpdate}
              disabled={updating}
            >
              {updating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Save Updates
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

